import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ChatMessage, QuickAction } from './types';
import { sendChatMessage, saveChatToLocalStorage, loadChatFromLocalStorage } from './chatService';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';

// Sample quick actions
const defaultQuickActions: QuickAction[] = [
  { id: '1', text: 'Find a Bible Verse' },
  { id: '2', text: 'Help Me Pray' },
  { id: '3', text: 'Explain a Bible Word' },
  { id: '4', text: 'Answer a Tough Question' },
];

// Define the context shape
interface ChatContextType {
  messages: ChatMessage[];
  sendMessage: (message: string) => Promise<void>;
  loading: boolean;
  isChatOpen: boolean;
  toggleChat: () => void;
  setChatOpen: (isOpen: boolean) => void;
  quickActions: QuickAction[];
  clearHistory: () => void;
  chatState: {
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
  };
  clearChat: () => void;
}

// Create the context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider component
interface ChatContextProviderProps {
  children: ReactNode;
}

export const ChatContextProvider: React.FC<ChatContextProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [quickActions] = useState<QuickAction[]>(defaultQuickActions);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Create a chatState object that combines various states
  const chatState = {
    messages,
    isLoading: loading,
    error
  };

  // Load chat history from local storage on component mount
  useEffect(() => {
    const savedMessages = loadChatFromLocalStorage();
    if (savedMessages && savedMessages.length > 0) {
      setMessages(savedMessages);
    } else {
      // Add welcome message if no history
      setMessages([
        {
          id: uuidv4(),
          content: "Welcome to Selah's Chat Assistant! How can I help you with your faith journey today?",
          role: 'system',
          timestamp: Date.now(),
        },
      ]);
    }
  }, []);

  // Save messages to local storage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatToLocalStorage(messages);
    }
  }, [messages]);

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;

    // Add user message to the chat
    const userMessage: ChatMessage = {
      id: uuidv4(),
      content: message,
      role: 'user',
      timestamp: Date.now(),
    };
    
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLoading(true);
    setError(null);

    try {
      // Send message to the assistant with conversation history and get response
      const assistantMessage = await sendChatMessage(
        message, 
        messages.filter(msg => msg.role !== 'system') // Filter out system messages
      );
      
      // Add assistant message to the chat
      setMessages((prevMessages) => [...prevMessages, assistantMessage]);
      
      // Extract recommended tools
      if (assistantMessage.recommendedTools && assistantMessage.recommendedTools.length > 0) {
        console.log("Recommended tools:", assistantMessage.recommendedTools);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to get a response. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to get a response. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
  };
  
  const setChatOpen = (isOpen: boolean) => {
    setIsChatOpen(isOpen);
  };

  const clearHistory = () => {
    clearChat();
  };
  
  const clearChat = () => {
    // Add welcome message after clearing
    setMessages([
      {
        id: uuidv4(),
        content: "Welcome to Selah's Chat Assistant! How can I help you with your faith journey today?",
        role: 'system',
        timestamp: Date.now(),
      },
    ]);
    
    // Clear local storage
    localStorage.removeItem("selah_chat_history");
    
    toast({
      title: "Chat history cleared",
      description: "Your conversation history has been cleared.",
      duration: 2000, // Auto-dismiss after 2 seconds
    });
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        sendMessage,
        loading,
        isChatOpen,
        toggleChat,
        setChatOpen,
        quickActions,
        clearHistory,
        chatState,
        clearChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

// Export the ChatProvider for use in LiveChatWidget
export const ChatProvider = ChatContextProvider;

// Custom hook to use the chat context
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatContextProvider');
  }
  return context;
};
